/**
 * Client service for GitHub repo symbol index (V2).
 */

const API_BASE = '';

export interface RepoSymbolRecord {
  file_path: string;
  symbol_name: string;
  symbol_kind: string;
  line_start?: number;
  line_end?: number;
  exported?: boolean;
  signature?: string;
  parent_symbol?: string;
  metadata?: Record<string, unknown>;
}

export interface RepoGraphEdgeRecord {
  from_file: string;
  from_symbol?: string;
  to_file: string;
  to_symbol?: string;
  edge_type: string;
  confidence?: number;
  metadata?: Record<string, unknown>;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getCurrentSession } = await import('@/lib/supabase');
  const session = await getCurrentSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  };
}

export async function parseSymbolsOnServer(
  owner: string,
  repo: string,
  files: Array<{ path: string; content: string }>,
  repoTreePaths: string[]
): Promise<{ symbols: RepoSymbolRecord[]; edges: RepoGraphEdgeRecord[] }> {
  if (files.length === 0) return { symbols: [], edges: [] };
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/parse-symbols`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ files, repoTreePaths }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`parse-symbols failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function storeRepoSymbols(
  owner: string,
  repo: string,
  symbols: RepoSymbolRecord[],
  replacePaths?: string[]
): Promise<void> {
  if (symbols.length === 0 && (!replacePaths || replacePaths.length === 0)) return;
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/store-symbols`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ symbols, replacePaths }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`store-symbols failed: ${res.status} ${text}`);
  }
}

export async function storeRepoGraphEdges(
  owner: string,
  repo: string,
  edges: RepoGraphEdgeRecord[],
  replacePaths?: string[]
): Promise<void> {
  if (edges.length === 0 && (!replacePaths || replacePaths.length === 0)) return;
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/github/repos/${owner}/${repo}/store-graph-edges`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ edges, replacePaths }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`store-graph-edges failed: ${res.status} ${text}`);
  }
}

const PARSEABLE_EXT = new Set(['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs']);

function isParseablePath(path: string): boolean {
  const dot = path.lastIndexOf('.');
  return dot >= 0 && PARSEABLE_EXT.has(path.slice(dot).toLowerCase());
}

export async function indexSymbolsAndGraphBatch(
  owner: string,
  repo: string,
  files: Array<{ path: string; content: string }>,
  repoTreePaths: string[],
  replacePaths?: string[]
): Promise<{ symbolsStored: number; edgesStored: number }> {
  const parseable = files.filter(f => isParseablePath(f.path) && f.content.trim());
  if (parseable.length === 0 && (!replacePaths || replacePaths.length === 0)) {
    return { symbolsStored: 0, edgesStored: 0 };
  }
  try {
    const { symbols, edges } =
      parseable.length > 0
        ? await parseSymbolsOnServer(owner, repo, parseable, repoTreePaths)
        : { symbols: [], edges: [] };
    const pathsToReplace = replacePaths ?? parseable.map(f => f.path);
    await Promise.all([
      storeRepoSymbols(owner, repo, symbols, pathsToReplace),
      storeRepoGraphEdges(owner, repo, edges, pathsToReplace),
    ]);
    return { symbolsStored: symbols.length, edgesStored: edges.length };
  } catch (err) {
    console.warn('[RepoSymbolService] indexSymbolsAndGraphBatch failed:', err);
    return { symbolsStored: 0, edgesStored: 0 };
  }
}
